import React, { useState, useEffect, useCallback } from 'react';
import AdminSidebar from './components/AdminSidebar';
import AdminLoginPage from './AdminLoginPage';
import AdminPage from '../AdminPage';
import ChatPage from '../ChatPage';
import api from '../../api';
import { Menu, Shield, ExternalLink, RefreshCw } from 'lucide-react';
import { Toaster, toast } from 'react-hot-toast';

const AdminApp = () => {
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('users');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const isStaff = userProfile && ['admin', 'moderator', 'support'].includes(userProfile.role);

    const fetchProfile = useCallback(async () => {
        const isLoggedIn = localStorage.getItem('is_logged_in');
        if (!isLoggedIn) {
            setLoading(false);
            return;
        }

        try {
            const res = await api.get('/auth/me');
            setUserProfile(res.data);
        } catch (err) {
            console.error("Erro ao verificar sessão da equipe:", err);
            setUserProfile(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setSidebarOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleLogout = async () => {
        try {
            await api.post('/auth/logout');
        } catch (e) {
            console.error('Logout error', e);
        }
        localStorage.removeItem('is_logged_in');
        localStorage.removeItem('admin_token');
        setUserProfile(null);
        toast.success("Sessão administrativa finalizada.");
    };

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: '#090d16',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Outfit, sans-serif'
            }}>
                <div style={{
                    width: '48px',
                    height: '48px',
                    border: '3px solid #1e293b',
                    borderTop: '3px solid #9333ea',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                }} />
                <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: '16px', fontWeight: '600' }}>
                    Carregando ambiente administrativo...
                </p>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // Se não estiver logado ou a role for comum (user), mostra a tela de login administrativo
    if (!isStaff) {
        return (
            <>
                <AdminLoginPage onLoginSuccess={(u) => setUserProfile(u)} />
                <Toaster position="top-right" />
            </>
        );
    }

    const renderMainContent = () => {
        if (activeTab === 'chat') {
            return (
                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '15px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                    <ChatPage />
                </div>
            );
        }

        return (
            <AdminPage
                userPlan={userProfile.role}
                initialTab={activeTab}
                onTabChange={setActiveTab}
            />
        );
    };

    const mainStyle = isMobile ? {
        flex: 1,
        padding: '20px 14px',
        marginLeft: 0,
        paddingTop: '75px',
        boxSizing: 'border-box',
        width: '100%'
    } : {
        flex: 1,
        padding: '30px 40px',
        marginLeft: '260px',
        boxSizing: 'border-box',
        maxWidth: 'calc(100vw - 260px)'
    };

    return (
        <div style={{
            display: 'flex',
            minHeight: '100vh',
            backgroundColor: '#f8fafc',
            fontFamily: 'Outfit, sans-serif'
        }}>
            {/* Backdrop Mobile */}
            {isMobile && sidebarOpen && (
                <div
                    onClick={() => setSidebarOpen(false)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.7)',
                        backdropFilter: 'blur(4px)',
                        zIndex: 950
                    }}
                />
            )}

            {/* Header Mobile */}
            {isMobile && (
                <header style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '60px',
                    backgroundColor: '#090d16',
                    borderBottom: '1px solid #1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 16px',
                    zIndex: 900
                }}>
                    <button
                        onClick={() => setSidebarOpen(true)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#cbd5e1',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Menu size={24} />
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Shield size={18} color="#a855f7" />
                        <span style={{ color: '#fff', fontSize: '15px', fontWeight: '800' }}>Admin Gestão</span>
                    </div>

                    <button
                        onClick={() => window.location.href = '/'}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#94a3b8',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        title="Ir para o App de Afiliados"
                    >
                        <ExternalLink size={18} />
                    </button>
                </header>
            )}

            {/* Sidebar Exclusiva do Admin */}
            <AdminSidebar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                handleLogout={handleLogout}
                isMobile={isMobile}
                userProfile={userProfile}
            />

            {/* Conteúdo Principal */}
            <main style={mainStyle}>
                {/* Header Topo do Desktop */}
                {!isMobile && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '28px',
                        paddingBottom: '16px',
                        borderBottom: '1px solid #e2e8f0'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '10px',
                                backgroundColor: '#f3e8ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Shield size={20} color="#9333ea" />
                            </div>
                            <div>
                                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
                                    Painel Administrativo & Gestão
                                </h2>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>
                                    Ambiente isolado • Conectado como <strong>{userProfile.username}</strong> ({userProfile.role.toUpperCase()})
                                </span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <button
                                onClick={() => window.location.reload()}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '8px 14px',
                                    backgroundColor: '#fff',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    color: '#475569',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                            >
                                <RefreshCw size={14} /> Atualizar
                            </button>

                            <button
                                onClick={() => window.location.href = '/'}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '8px 14px',
                                    backgroundColor: '#0f172a',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: '#fff',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'background 0.15s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1e293b'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0f172a'}
                            >
                                <ExternalLink size={14} /> Abrir App de Afiliados
                            </button>
                        </div>
                    </div>
                )}

                {renderMainContent()}
            </main>

            <Toaster position="top-right" />
        </div>
    );
};

export default AdminApp;
